import { useState } from "react";
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import type {
  MergePersonRequest,
  PersonId,
  PersonRecord,
  UnmergePersonRequest,
} from "@thalamos/haven-common";
import {
  displayName,
  MergePersonRequestSchema,
  UnmergePersonRequestSchema,
} from "@thalamos/haven-common";

import { DirectoryFilter } from "../../pages/Admin/DirectoryFilter.js";
import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";

/**
 * Whether a merge, or its unmerge, landed. One `failed` for every way it did
 * not: nothing the person in front of the dialog could do differently
 * distinguishes a refusal from an unreachable server.
 */
export type MergeOutcome = "merged" | "failed";

/**
 * The message key a parsed request's reason is refused with, or `undefined`
 * for one that will do, so the dialog cannot send a reason the endpoint
 * rejects.
 */
const reasonError = (
  parsed: z.ZodSafeParseResult<unknown>,
): string | undefined =>
  parsed.success
    ? undefined
    : parsed.error.issues.find((issue) => issue.path[0] === "reason")?.message;

/**
 * Finds the duplicate record and says why it is the same person.
 *
 * Controlled from outside: the page owns the search so it can debounce and fetch,
 * and the dialog owns only what has been picked and typed. `onMerge` is injected,
 * so a story shows the dialog against records it chose.
 */
export const MergeRecordDialog = ({
  open,
  onClose,
  searchValue,
  onSearchChange,
  candidates,
  candidatesLoading,
  onMerge,
}: {
  open: boolean;
  onClose: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Records the merge may be made from — never the record being merged into. */
  candidates: PersonRecord[];
  candidatesLoading: boolean;
  onMerge: (request: MergePersonRequest) => Promise<MergeOutcome>;
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<PersonRecord | undefined>(undefined);
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [status, setStatus] = useState<"submitting" | "failed" | undefined>(
    undefined,
  );

  const parsed =
    selected === undefined
      ? undefined
      : MergePersonRequestSchema.safeParse({
          sourcePersonId: selected.id,
          reason,
        });
  const error = parsed === undefined ? undefined : reasonError(parsed);

  const reset = () => {
    setSelected(undefined);
    setReason("");
    setReasonTouched(false);
    setStatus(undefined);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setReasonTouched(true);

    if (parsed === undefined || error !== undefined) {
      return;
    }

    // Unreachable: a refused reason has returned above through `reasonError`,
    // and the id comes from a parsed person record. Kept to fail loudly if not.
    /* v8 ignore if */
    if (!parsed.success) {
      throw new Error("Expected a merge request with a valid reason to parse", {
        cause: parsed.error,
      });
    }

    setStatus("submitting");
    const outcome = await onMerge(parsed.data).catch(
      (): MergeOutcome => "failed",
    );

    if (outcome === "merged") {
      close();
      return;
    }

    setStatus("failed");
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{t("personPage.merges.dialogTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {t("personPage.merges.dialogDescription")}
          </DialogContentText>

          <DirectoryFilter
            value={searchValue}
            label={t("personPage.merges.search")}
            onChange={onSearchChange}
          />

          {candidatesLoading ? (
            <CircularProgress
              size={24}
              role="status"
              aria-label={t("common.loading")}
            />
          ) : candidates.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("personPage.merges.noMatches")}
            </Typography>
          ) : (
            <List dense disablePadding>
              {candidates.map((candidate) => (
                <ListItemButton
                  key={candidate.id}
                  selected={selected?.id === candidate.id}
                  onClick={() => {
                    setSelected(candidate);
                  }}
                >
                  <ListItemText
                    primary={displayName(candidate.names)}
                    secondary={
                      candidate.dateOfBirth ?? t("personListing.notRecorded")
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          {selected === undefined ? null : (
            <Stack spacing={2}>
              <Alert severity="info">
                {t("personPage.merges.selected", {
                  name: displayName(selected.names),
                })}
              </Alert>
              <TextField
                id="merge-reason"
                label={t("personPage.merges.reason")}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                onBlur={() => {
                  setReasonTouched(true);
                }}
                error={reasonTouched && error !== undefined}
                helperText={
                  reasonTouched && error !== undefined
                    ? t(error)
                    : t("personPage.merges.reasonHelp")
                }
                multiline
                minRows={2}
                fullWidth
              />
            </Stack>
          )}

          {status === "failed" ? (
            <Alert severity="error">{t("personPage.merges.failed")}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          label: t("personPage.merges.cancel"),
          onClick: close,
        }}
        proceed={{
          label: t("personPage.merges.confirm"),
          onClick: submit,
          disabled: selected === undefined || status === "submitting",
          kind: "primary",
        }}
      />
    </Dialog>
  );
};

/**
 * Says why a merge is being undone. The record named is the one that will stand
 * on its own again; `null` closes the dialog.
 */
export const UndoMergeDialog = ({
  record,
  onClose,
  onUndo,
}: {
  record: PersonRecord | null;
  onClose: () => void;
  onUndo: (
    sourcePersonId: PersonId,
    request: UnmergePersonRequest,
  ) => Promise<MergeOutcome>;
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [reasonTouched, setReasonTouched] = useState(false);
  const [status, setStatus] = useState<"submitting" | "failed" | undefined>(
    undefined,
  );

  const parsed = UnmergePersonRequestSchema.safeParse({ reason });
  const error = reasonError(parsed);

  const close = () => {
    setReason("");
    setReasonTouched(false);
    setStatus(undefined);
    onClose();
  };

  const submit = async () => {
    setReasonTouched(true);

    if (record === null || error !== undefined) {
      return;
    }

    // Unreachable: the request holds only the reason, and a refused reason has
    // returned above through `reasonError`. Kept to fail loudly if not.
    /* v8 ignore if */
    if (!parsed.success) {
      throw new Error(
        "Expected an unmerge request with a valid reason to parse",
        {
          cause: parsed.error,
        },
      );
    }

    setStatus("submitting");
    const outcome = await onUndo(record.id, parsed.data).catch(
      (): MergeOutcome => "failed",
    );

    if (outcome === "merged") {
      close();
      return;
    }

    setStatus("failed");
  };

  return (
    <Dialog open={record !== null} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{t("personPage.merges.undoTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>
            {t("personPage.merges.undoDescription")}
          </DialogContentText>
          {record === null ? null : (
            <Typography variant="subtitle1">
              {displayName(record.names)}
            </Typography>
          )}
          <TextField
            id="undo-merge-reason"
            label={t("personPage.merges.reason")}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
            }}
            onBlur={() => {
              setReasonTouched(true);
            }}
            error={reasonTouched && error !== undefined}
            helperText={
              reasonTouched && error !== undefined
                ? t(error)
                : t("personPage.merges.reasonHelp")
            }
            multiline
            minRows={2}
            fullWidth
          />
          {status === "failed" ? (
            <Alert severity="error">{t("personPage.merges.undoFailed")}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          label: t("personPage.merges.cancel"),
          onClick: close,
        }}
        proceed={{
          label: t("personPage.merges.undoConfirm"),
          onClick: submit,
          disabled: status === "submitting",
          kind: "destructive",
        }}
      />
    </Dialog>
  );
};
